import { useState, useEffect, useRef } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { toast } from "sonner";
import { useTranslations } from "@/common/hooks/use-translations";

/** Authored attributes a code element keeps: its anchor id and direction. */
export interface CodeAttributes {
  id?: string;
  dir?: "ltr" | "rtl" | "auto";
}

interface CodeBlockProps {
  code: string;
  language?: string;
  inline?: boolean;
  preAttributes?: CodeAttributes;
  codeAttributes?: CodeAttributes;
}

export function CodeBlock({
  code,
  language,
  inline,
  preAttributes,
  codeAttributes,
}: CodeBlockProps) {
  const { t } = useTranslations();
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(t("common.copiedToClipboard"));
      // Clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("common.copyFailed"));
    }
  };

  // Inline code
  if (inline) {
    return (
      <code
        {...codeAttributes}
        className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono"
      >
        {code}
      </code>
    );
  }

  // Block code
  return (
    <div className="relative group my-4 max-w-full">
      <div className="flex items-center justify-between bg-muted px-4 py-2 rounded-t-lg border border-border">
        {language && (
          <span className="text-xs text-muted-foreground font-mono uppercase">
            {language}
          </span>
        )}
        {!language && <div />}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleCopy}
          // Revealed on hover, and on keyboard focus — otherwise Tab lands on a
          // fully transparent button whose focus ring is invisible too.
          className="h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
          aria-label={t("common.copyCode")}
        >
          {copied ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
      </div>
      <pre
        {...preAttributes}
        className="overflow-x-auto p-4 rounded-b-lg bg-muted border border-t-0 border-border !my-0"
      >
        <code
          {...codeAttributes}
          className={language ? `language-${language}` : undefined}
        >
          {code}
        </code>
      </pre>
    </div>
  );
}
